import { Link } from "react-router"
import { useSponsorPortal } from "./sponsor-session-context"
import { DonorEmptyState } from "./components/empty-state"
import { SponsorshipStatusCard } from "./components/sponsorship-status"

export function DonorChildrenPage() {
  const portal = useSponsorPortal()
  if (!portal) return null

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h1 className="m-0 text-ink font-display text-[32px] font-bold leading-normal">My children</h1>
        <p className="m-0 text-ink text-lg font-normal leading-normal">The scholars you're supporting, and the story of their year.</p>
      </div>

      <div className="flex flex-col gap-5">
        {portal.children.length === 0 && portal.sponsorshipRequest ? (
          <SponsorshipStatusCard
            request={portal.sponsorshipRequest}
            matchState={portal.matchState}
            activeMatchCount={portal.activeMatchCount}
          />
        ) : portal.children.length === 0 ? (
          <DonorEmptyState
            title={portal.sponsorshipRequest
              ? "Your sponsorship request is in progress"
              : portal.donations.length > 0 ? "Your sponsorship is being matched" : "Your sponsorship story starts here"
            }
            description={portal.sponsorshipRequest
              ? "See the latest status and next steps for your sponsorship request on the request page."
              : portal.donations.length > 0
              ? "We are connecting your contribution with a child. We will show their profile and updates here as soon as the match is confirmed."
              : "Choose a sponsorship to begin. Once a child is linked to your account, their profile, reports, and progress will appear here."
            }
            primaryLabel={portal.sponsorshipRequest
              ? "View sponsorship status"
              : portal.donations.length > 0 ? "Contact Redempta" : "Start a sponsorship"
            }
            primaryTo={portal.sponsorshipRequest
              ? "/donor/request"
              : portal.donations.length > 0 ? "/contact" : "/sponsor"
            }
          />
        ) : portal.children.map((child) => (
          <article key={child.id} className="border border-gold-500 rounded-[10px] bg-white overflow-hidden">
            {/* Child Info Section */}
            <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-[auto_1fr] sm:gap-6 sm:p-8">
              {child.photoUrl && (
                <img
                  src={child.photoUrl}
                  alt={child.name}
                  className="w-24 h-24 rounded-[10px] object-cover sm:w-40 sm:h-40"
                />
              )}
              <div className="flex flex-col">
                <h2 className="m-0 text-[#0b140d] font-display text-2xl font-bold leading-8 tracking-tight">
                  {child.name}
                </h2>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-3 sm:grid-cols-4 sm:gap-y-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[rgba(9,49,27,0.7)] text-[13px] font-medium">Age</span>
                    <span className="text-[#0b140d] text-sm font-semibold">{child.age}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[rgba(9,49,27,0.7)] text-[13px] font-medium">Class</span>
                    <span className="text-[#0b140d] text-sm font-semibold">{child.classLevel}</span>
                  </div>
                  <div className="col-span-2 flex flex-col gap-0.5">
                    <span className="text-[rgba(9,49,27,0.7)] text-[13px] font-medium">School</span>
                    <span className="text-[#0b140d] text-sm font-semibold">{child.school}</span>
                  </div>
                  <div className="col-span-2 flex flex-col gap-0.5 mt-2">
                    <span className="text-[rgba(9,49,27,0.7)] text-[13px] font-medium">Sponsoring since</span>
                    <span className="text-[#0b140d] text-sm font-semibold">
                      {child.since ? new Date(child.since).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "Not available"}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/donor/children/${child.id}`}
                  className="mt-4 text-sm font-medium text-forest-700 hover:underline"
                >
                  View profile, results & documents
                </Link>
              </div>
            </div>

            {/* Updates Section */}
            <div className="border-t border-gold-500 bg-cream-50 px-5 py-5 sm:px-8 sm:py-6">
              <h3 className="m-0 text-[#09311b] text-xs font-normal uppercase tracking-[0.1em]">Updates</h3>
              <ul className="flex flex-col gap-3 mt-3 p-0 list-none">
                {child.updates.length === 0 ? (
                  <li className="text-sm text-ink/60">No reports uploaded yet.</li>
                ) : child.updates.map((update) => (
                  <li key={update.date} className="flex flex-col gap-1 py-4 sm:flex-row sm:items-start sm:gap-6">
                    <time className="text-[#4e594f] text-[13px] font-medium sm:w-32 sm:flex-shrink-0">
                      {new Date(update.date).toLocaleDateString()}
                    </time>
                    <p className="m-0 text-[#0b140d] text-sm font-semibold leading-5">
                      {update.description}{" "}
                      <a href={update.url} target="_blank" rel="noopener noreferrer" className="text-forest-700 underline font-normal">
                        View report
                      </a>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>
    </div>
  )
}
